import React from 'react';
import { Link } from 'react-router-dom';
import { FiClock, FiTag, FiArrowRight, FiPercent } from 'react-icons/fi';
import { getImageUrl } from '../utils/constants';

const PromotionCard = ({ promotion }) => {
  // Calculate discounted price if it's a product with discount
  const hasDiscount = promotion.discount_percent > 0;
  const discountedPrice = hasDiscount 
    ? promotion.price * (1 - promotion.discount_percent / 100)
    : promotion.price;
  
  // Fixed image URL construction
  const imageUrl = promotion.images?.[0] 
    ? getImageUrl(promotion.images[0].filepath, 'products')
    : 'https://via.placeholder.com/400x300/F3F4F6/9CA3AF?text=NAa+Collection+Promotion';
  
  const formattedDate = promotion.created_at 
    ? new Date(promotion.created_at).toLocaleDateString('km-KH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'ថ្មីៗ';

  // Clean description from HTML tags
  const cleanDescription = promotion.description?.replace(/<[^>]*>/g, '').substring(0, 120) || '';

  return (
    <div className="bg-white border border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300 group">
      <Link to={`/products/${promotion.id}`} className="block">
        {/* Promotion Image with Discount Badge */}
        <div className="relative overflow-hidden h-56 bg-gray-100">
          <img
            src={imageUrl}
            alt={promotion.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/400x300/F3F4F6/9CA3AF?text=Promotion";
            }}
          />
          
          {/* Discount Badge */}
          {hasDiscount && (
            <div className="absolute top-3 left-3 bg-black text-white px-3 py-1.5 shadow-lg">
              <div className="flex items-center gap-1">
                <FiPercent size={14} />
                <span className="text-sm font-bold">-{promotion.discount_percent}%</span>
              </div>
            </div>
          )}
          
          {/* Hot Badge for new promotions */}
          {!hasDiscount && (
            <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1.5 shadow-lg">
              <span className="text-xs font-bold">ថ្មី</span>
            </div>
          )}
          
          {/* Flash Sale Badge */}
          <div className="absolute top-3 right-3 bg-yellow-500 text-black px-2 py-1 shadow-lg animate-pulse">
            <span className="text-xs font-bold">FLASH SALE</span>
          </div>
        </div>

        {/* Promotion Content */}
        <div className="p-4">
          {/* Category Tag */}
          {promotion.category?.name && (
            <div className="mb-2">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1">
                {promotion.category.name}
              </span>
            </div>
          )}
          
          <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-black transition-colors min-h-[3rem]">
            {promotion.title}
          </h3>
          
          <p className="text-gray-600 text-xs mb-3 line-clamp-2 leading-relaxed">
            {cleanDescription}
            {promotion.description?.length > 120 && '...'}
          </p>

          {/* Price Section for Products */}
          {promotion.price && (
            <div className="mb-3">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xl font-bold text-black">
                  ${discountedPrice.toFixed(2)}
                </span>
                {hasDiscount && (
                  <span className="text-xs text-gray-400 line-through">
                    ${promotion.price.toFixed(2)}
                  </span>
                )}
              </div>
              
              {/* Save amount */}
              {hasDiscount && (
                <div className="text-xs text-green-600 mt-1">
                  សន្សំបាន ${(promotion.price - discountedPrice).toFixed(2)}
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-3 mt-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <FiTag className="w-3 h-3" />
                <span>ការប្រម៉ូសិន</span>
              </div>
              <div className="flex items-center gap-1">
                <FiClock className="w-3 h-3" />
                <span>{formattedDate}</span>
              </div>
            </div>
            
            <span className="text-black font-medium text-xs flex items-center gap-1 group-hover:gap-2 transition-all">
              ទិញឥឡូវ
              <FiArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </Link>
      
      {/* Quick Action Button */}
      <div className="px-4 pb-4">
        <button 
          onClick={(e) => {
            e.preventDefault();
            window.open(`https://t.me/nnick321`, '_blank');
          }}
          className="w-full bg-gray-100 text-gray-700 text-xs py-2 text-center hover:bg-black hover:text-white transition-colors"
        >
          សាកសួរតាម Telegram
        </button>
      </div>
    </div>
  );
};

export default PromotionCard;